import type { AuthApi } from "./auth_api.js";

/**
 * Partner links (`/p/<code>`, W-1). The code is checked once on arrival;
 * a valid one is kept for this tab in sessionStorage (`kn.partner`, not a
 * secret) and sent with sign-up, locked. Unknown or inactive codes are never
 * sent: sign-up shows an info banner instead and carries on. A lookup that
 * fails (network, 5xx) is "unchecked": the customer can retry, or continue
 * without it (same banner as an unknown code).
 */
export const PARTNER_STORAGE_KEY = "kn.partner";

const CODE_RE = /^[a-z0-9][a-z0-9_-]{0,63}$/;

export type PartnerCheck =
  | { status: "valid"; code: string }
  | { status: "unknown" }
  | { status: "unchecked" };

export function normalizePartnerCode(raw: string): string | null {
  const code = raw.trim().toLowerCase();
  return CODE_RE.test(code) ? code : null;
}

/** Never throws: a failed lookup is "unchecked", not "unknown". */
export async function checkPartnerCode(api: Pick<AuthApi, "checkPartnerCode">, raw: string): Promise<PartnerCheck> {
  const code = normalizePartnerCode(raw);
  if (!code) return { status: "unknown" };
  try {
    const { valid } = await api.checkPartnerCode(code);
    return valid ? { status: "valid", code } : { status: "unknown" };
  } catch {
    return { status: "unchecked" };
  }
}
